import { Injectable, inject, signal, EventEmitter } from '@angular/core';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { escapeRegex } from '@/src/app/uygulamalar/yuz-tanima/utils/regex.utils';
import { IVT, IYollar, IIcerik, IKart, IKartTuru, IEkTuru } from '@/src/service/fikoyt-ui/interfaces/IKart.interface';
import { FikoYTId } from '@/src/service/fikoyt-ui/helpers/FikoYTId';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { IParametreSetiGrubu } from '@/src/service/fikoyt-ui/interfaces/IParametreSeti.interface';

export interface Proje {
  _id?: string;
  projeAdi: string;
  aciklama?: string;
  baslangicTarihi?: string;
  bitisTarihi?: string;
  durum?: 'planlama' | 'devam-ediyor' | 'tamamlandi' | 'beklemede' | 'iptal';
  oncelik?: 'dusuk' | 'normal' | 'yuksek' | 'acil';
  yuzdeTamamlanma?: number;
  butce?: number;
  harcama?: number;
  sorumluKisi?: string;
  ekipUyeleri?: string[];
  musteriFirma?: string;
  etiketler?: string[];
  notlar?: string;
  olusturmaTarihi?: string;
  guncellemeTarihi?: string;
  parametreler?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class ProjelerService {
  private FikoYT = inject(FikoYTService);
  projeler = signal<Proje[]>([]);
  yukleniyor = signal<boolean>(false);
  hata = signal<string | null>(null);

  // Legacy uyumluluk için
  paylasimYapildi: EventEmitter<any> = new EventEmitter(true);
  mesgul: boolean = false;
  ilerleme = 0;
  u_id: string | any = null;
  aktifIcerikNo: number = 0;
  kart: IKart = {
    _id: new FikoYTId().toString(),
    vt: "FikoYT.sistem.hareketler",
    yol: "FikoYT.sistem.hareketler.kalem",
    turu: IKartTuru.KART,
    gizlilik: "Herkese Açık",
    kisiler: [],
    veri: {
      solSimge: {
        aciklama: "FikoYT Bayrak Açıklaması",
        baslik: "FikoYT",
        renkArka: undefined,
        renkYazi: undefined,
      },
      kurdela: {
        aciklama: "FikoYT Kurdela Açıklaması",
        baslik: "FikoYT",
        renkArka: undefined,
        renkYazi: undefined,
      },
      icerikler: [
        {
          _id: new FikoYTId().toString(),
          html: '',
          aciklama: '',
          sifre: null,
        } as IIcerik
      ]
    }
  };

  readonly parametreSetleri: IParametreSetiGrubu[] = [
    {
      id: 'uyar.proje',
      baslik: 'Projeler',
      simge: '📁',
      renk: 'linear-gradient(135deg, #7c2d12, #fa8c16)',
      kaynak: 'kod',
      bilesen: 'projeler',
      altGruplar: [
        {
          id: 'uyar.proje.kategori',
          baslik: 'Kategori',
          simge: '📂',
          parametreler: [
            { turu: 'Yazı', ismi: 'kategoriAdi', baslik: 'Kategori Adı', ayarlar: { zorunlu: true } },
            { turu: 'Renk', ismi: 'renk', baslik: 'Renk' },
            { turu: 'Açılır Kutu', ismi: 'oncelik', baslik: 'Öncelik', ayarlar: { secenekler: ['Düşük', 'Normal', 'Yüksek', 'Kritik'] } },
          ],
        },
        {
          id: 'uyar.proje.ekle',
          baslik: 'Ekle',
          simge: '➕',
          parametreler: [
            { turu: 'Yazı', ismi: 'projeAdi', baslik: 'Proje Adı', ayarlar: { zorunlu: true } },
            { turu: 'Yazı Alanı', ismi: 'aciklama', baslik: 'Açıklama' },
            { turu: 'Tarih', ismi: 'baslangic', baslik: 'Başlangıç Tarihi' },
            { turu: 'Tarih', ismi: 'bitis', baslik: 'Bitiş Tarihi' },
          ],
          sayfaKurallari: { yeniSayfaEngelle: true },
        },
      ],
    },
    {
      id: 'uyar.basit-not',
      baslik: 'Basit Not',
      simge: '📋',
      renk: 'linear-gradient(135deg, #064e3b, #52c41a)',
      kaynak: 'kod',
      parametreler: [
        { turu: 'Yazı', ismi: 'baslik', baslik: 'Not Başlığı' },
        { turu: 'Açılır Kutu', ismi: 'kategori', baslik: 'Kategori', ayarlar: { secenekler: ['Genel', 'Acil', 'Bilgi', 'Hatırlatma'] } },
      ],
    },
  ];

  constructor() {}

  // Legacy metodlar
  icerikEkle(ek: any = null) {
    this.kart?.veri?.icerikler?.push({ _id: new FikoYTId().toString(), aciklama: '', html: '', ek: ek, sifre: null }) ?? 1 - 1;
  }

  sifirla() {
    this.kart = {
      _id: new FikoYTId().toString(),
      vt: "FikoYT.sistem.hareketler",
      yol: "FikoYT.sistem.hareketler.kalem",
      turu: IKartTuru.KART,
      gizlilik: "Herkese Açık",
      kisiler: [],
      veri: {
        solSimge: {
          aciklama: undefined,
          baslik: "FikoYT",
          renkArka: undefined,
          renkYazi: undefined,
        },
        kurdela: {
          aciklama: undefined,
          baslik: "FikoYT",
          renkArka: undefined,
          renkYazi: undefined,
        },
        icerikler: []
      }
    };
    this.icerikEkle();
  }

  aktifEtSonSayfa() {
    this.aktifIcerikNo = (this.kart?.veri?.icerikler?.length ?? 1) - 1;
  }

  /** Kartı kopyalayarak paylaş kutusuna yükler */
  kartKopyalaPaylasimaAc(kaynakKart: IKart) {
    const kopya: IKart = JSON.parse(JSON.stringify(kaynakKart));
    const orijinalSahip = kopya.sahip;
    const orijinalKartId = kopya._id;
    const orijinalTarih = (kopya as any).tarih;
    kopya._id = new FikoYTId().toString();
    delete (kopya as any).tarih;
    delete (kopya as any).sahip;
    delete (kopya as any).silindi;
    delete (kopya as any).begenenler;
    delete (kopya as any).begenmeSayisi;
    delete (kopya as any).ifadeler;
    delete (kopya as any).ifadeSayilari;
    delete (kopya as any).yorumSayisi;
    delete (kopya as any).hareketler;
    delete (kopya as any).sabitlendi;
    delete (kopya as any).duzenlenmeTarihi;
    if (kopya.veri?.icerikler) {
      // Sahip bilgilerini al (profil resmi ve isim için)
      const aktifKullanici = this.FikoYT.kimlik.aktifKullaniciJson();
      const sahipIsim = orijinalSahip === aktifKullanici?._id
        ? (aktifKullanici?.isim ?? '') + ' ' + (aktifKullanici?.soyIsim ?? '')
        : '';

      const referansDisiTurler = new Set([
        IEkTuru.EKREFERANS, IEkTuru.REFERANS, IEkTuru.YOUTUBE,
        IEkTuru.UYARDOKUM, IEkTuru.YAPAYZEKA, IEkTuru.YOK, IEkTuru.ACIKLAMA
      ]);
      for (const icerik of kopya.veri.icerikler) {
        icerik._id = new FikoYTId().toString();
        // Sunucudan gelen medya eklerini EKREFERANS'a dönüştür
        const ek = (icerik as any)?.ek;
        if (ek) {
          // JSON kopyası sonrası File/SafeUrl artıkları temizle (gerçek dosya olamaz)
          delete ek.dosya;
          delete ek.dosyaObjectUrl;
          delete ek.resimObjectUrl;
          delete ek.kokDosya;
          delete ek.kokDosyaObjectUrl;
        }
        if (ek?._id && ek?.turu && !referansDisiTurler.has(ek.turu)) {
          if (!ek.bilgiler) ek.bilgiler = {};
          ek.bilgiler.orijinalTuru = ek.turu;
          ek.bilgiler.sahip = orijinalSahip;
          ek.bilgiler.sahipIsim = sahipIsim.trim();
          ek.bilgiler.kartId = orijinalKartId;
          if (orijinalTarih) ek.bilgiler.orijinalTarih = orijinalTarih;
          ek.bilgiler.referansSayisi = 1;
          ek.turu = IEkTuru.EKREFERANS;
        }
        // Zaten EKREFERANS olan kartlarda eksik alanları doldur ve sayacı artır
        if (ek?.turu === IEkTuru.EKREFERANS && ek?.bilgiler) {
          if (!ek.bilgiler.kartId) ek.bilgiler.kartId = orijinalKartId;
          if (!ek.bilgiler.orijinalTarih && orijinalTarih) ek.bilgiler.orijinalTarih = orijinalTarih;
          if (!ek.bilgiler.sahip) ek.bilgiler.sahip = orijinalSahip;
          if (!ek.bilgiler.sahipIsim && sahipIsim.trim()) ek.bilgiler.sahipIsim = sahipIsim.trim();
          ek.bilgiler.referansSayisi = (ek.bilgiler.referansSayisi || 0) + 1;
        }
      }
    }
    this.kart = kopya;
    this.aktifIcerikNo = 0;
  }

  paylas() {
    // Legacy method
  }

  // Tüm projeleri getir
  async projeleriGetir(): Promise<Proje[]> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        aggregate: [
          {
            $match: {
              vt: 'FikoYT.Uyar.projeler' as IVT,
              yol: 'FikoYT.Uyar.projeler.kalem' as IYollar
            }
          },
          {
            $sort: { olusturmaTarihi: -1 }
          }
        ]
      };

      await istek.isteSync();

      if (istek.cevap.sonuc) {
        this.projeler.set(istek.cevap.sonuc);
        return istek.cevap.sonuc;
      }

      return [];
    } catch (error) {
      console.error('Projeler getirilirken hata:', error);
      this.hata.set('Projeler yüklenemedi');
      return [];
    } finally {
      this.yukleniyor.set(false);
    }
  }

  // Tek bir proje getir
  async projeGetir(id: string): Promise<Proje | null> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        aggregate: [
          {
            $match: {
              _id: id,
              vt: 'FikoYT.Uyar.projeler' as IVT,
              yol: 'FikoYT.Uyar.projeler.kalem' as IYollar
            }
          }
        ]
      };

      await istek.isteSync();

      if (istek.cevap.sonuc && istek.cevap.sonuc.length > 0) {
        return istek.cevap.sonuc[0];
      }

      return null;
    } catch (error) {
      console.error('Proje getirilirken hata:', error);
      this.hata.set('Proje yüklenemedi');
      return null;
    } finally {
      this.yukleniyor.set(false);
    }
  }

  // Yeni proje oluştur
  async projeOlustur(proje: Proje): Promise<string | null> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const yeniProje: Proje = {
        _id: new FikoYTId().toString(),
        ...proje,
        olusturmaTarihi: new Date().toISOString(),
        guncellemeTarihi: new Date().toISOString(),
        durum: proje.durum || 'planlama',
        oncelik: proje.oncelik || 'normal',
        yuzdeTamamlanma: proje.yuzdeTamamlanma || 0,
        butce: proje.butce || 0,
        harcama: proje.harcama || 0,
        etiketler: proje.etiketler || [],
        ekipUyeleri: proje.ekipUyeleri || []
      };

      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.ekle',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        vt: 'FikoYT.Uyar.projeler' as IVT,
        yol: 'FikoYT.Uyar.projeler.kalem' as IYollar,
        veri: yeniProje
      };

      await istek.isteSync();

      if (istek.cevap.sonuc) {
        await this.projeleriGetir();
        return yeniProje._id!;
      }

      return null;
    } catch (error) {
      console.error('Proje oluşturulurken hata:', error);
      this.hata.set('Proje oluşturulamadı');
      return null;
    } finally {
      this.yukleniyor.set(false);
    }
  }

  // Proje güncelle
  async projeGuncelle(params: { filter: any; veri: Partial<Proje>; upsert?: boolean }): Promise<boolean> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const guncelVeri = {
        ...params.veri,
        guncellemeTarihi: new Date().toISOString()
      };

      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.guncelle',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        filter: {
          ...params.filter,
          vt: 'FikoYT.Uyar.projeler' as IVT,
          yol: 'FikoYT.Uyar.projeler.kalem' as IYollar
        },
        veri: { $set: guncelVeri },
        upsert: params.upsert || false
      };

      await istek.isteSync();

      if (istek.cevap.sonuc !== undefined) {
        await this.projeleriGetir();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Proje güncellenirken hata:', error);
      this.hata.set('Proje güncellenemedi');
      return false;
    } finally {
      this.yukleniyor.set(false);
    }
  }

  // Proje sil
  async projeSil(id: string): Promise<boolean> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.sil',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        filter: {
          _id: id,
          vt: 'FikoYT.Uyar.projeler' as IVT,
          yol: 'FikoYT.Uyar.projeler.kalem' as IYollar
        }
      };

      await istek.isteSync();

      if (istek.cevap.sonuc !== undefined) {
        await this.projeleriGetir();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Proje silinirken hata:', error);
      this.hata.set('Proje silinemedi');
      return false;
    } finally {
      this.yukleniyor.set(false);
    }
  }

  // Durum güncelle
  async durumGuncelle(id: string, durum: Proje['durum']): Promise<boolean> {
    return await this.projeGuncelle({
      filter: { _id: id },
      veri: { durum }
    });
  }

  // İlerleme yüzdesi güncelle
  async ilerlemeGuncelle(id: string, yuzdeTamamlanma: number): Promise<boolean> {
    return await this.projeGuncelle({
      filter: { _id: id },
      veri: { yuzdeTamamlanma: Math.min(100, Math.max(0, yuzdeTamamlanma)) }
    });
  }

  // Bütçe güncelle
  async butceGuncelle(id: string, butce: number, harcama?: number): Promise<boolean> {
    const veri: Partial<Proje> = { butce };
    if (harcama !== undefined) {
      veri.harcama = harcama;
    }
    return await this.projeGuncelle({
      filter: { _id: id },
      veri
    });
  }

  // Ekip üyesi ekle
  async ekipUyesiEkle(id: string, uyeId: string): Promise<boolean> {
    const proje = await this.projeGetir(id);
    if (!proje) return false;

    const ekipUyeleri = proje.ekipUyeleri || [];
    if (!ekipUyeleri.includes(uyeId)) {
      ekipUyeleri.push(uyeId);
      return await this.projeGuncelle({
        filter: { _id: id },
        veri: { ekipUyeleri }
      });
    }

    return true;
  }

  // Ekip üyesi çıkar
  async ekipUyesiCikar(id: string, uyeId: string): Promise<boolean> {
    const proje = await this.projeGetir(id);
    if (!proje) return false;

    const ekipUyeleri = (proje.ekipUyeleri || []).filter(u => u !== uyeId);
    return await this.projeGuncelle({
      filter: { _id: id },
      veri: { ekipUyeleri }
    });
  }

  // Etiket ekle
  async etiketEkle(id: string, etiket: string): Promise<boolean> {
    const proje = await this.projeGetir(id);
    if (!proje) return false;

    const etiketler = proje.etiketler || [];
    if (!etiketler.includes(etiket)) {
      etiketler.push(etiket);
      return await this.projeGuncelle({
        filter: { _id: id },
        veri: { etiketler }
      });
    }

    return true;
  }

  // Etiket çıkar
  async etiketCikar(id: string, etiket: string): Promise<boolean> {
    const proje = await this.projeGetir(id);
    if (!proje) return false;

    const etiketler = (proje.etiketler || []).filter(e => e !== etiket);
    return await this.projeGuncelle({
      filter: { _id: id },
      veri: { etiketler }
    });
  }

  // Arama yap
  async projeAra(aramaMetni: string): Promise<Proje[]> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        aggregate: [
          {
            $match: {
              vt: 'FikoYT.Uyar.projeler' as IVT,
              yol: 'FikoYT.Uyar.projeler.kalem' as IYollar,
              $or: [
                { projeAdi: { $regex: escapeRegex(aramaMetni), $options: 'i' } },
                { aciklama: { $regex: escapeRegex(aramaMetni), $options: 'i' } },
                { musteriFirma: { $regex: escapeRegex(aramaMetni), $options: 'i' } },
                { sorumluKisi: { $regex: escapeRegex(aramaMetni), $options: 'i' } }
              ]
            }
          },
          {
            $sort: { olusturmaTarihi: -1 }
          }
        ]
      };

      await istek.isteSync();

      if (istek.cevap.sonuc) {
        return istek.cevap.sonuc;
      }

      return [];
    } catch (error) {
      console.error('Proje aranırken hata:', error);
      this.hata.set('Arama yapılamadı');
      return [];
    } finally {
      this.yukleniyor.set(false);
    }
  }

  // Duruma göre filtrele
  async durumFiltrele(durum: Proje['durum']): Promise<Proje[]> {
    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        gecikmeliCevap: false,
      });

      istek.parametreler = {
        aggregate: [
          {
            $match: {
              vt: 'FikoYT.Uyar.projeler' as IVT,
              yol: 'FikoYT.Uyar.projeler.kalem' as IYollar,
              durum: durum
            }
          },
          {
            $sort: { olusturmaTarihi: -1 }
          }
        ]
      };

      await istek.isteSync();

      if (istek.cevap.sonuc) {
        return istek.cevap.sonuc;
      }

      return [];
    } catch (error) {
      console.error('Projeler filtrelenirken hata:', error);
      this.hata.set('Filtreleme yapılamadı');
      return [];
    } finally {
      this.yukleniyor.set(false);
    }
  }
}

// Backward compatibility - eski kod için alias
export { ProjelerService as ServisUyarProjeler };
